import * as Sentry from "@sentry/react";
import type { AuthResponse, UserAuthResponse } from "@theirstory/common/src/api_types/backend/auth";
import axios from "axios";
import { SubmissionError } from "redux-form";
import { decode } from "jwt-simple";

import {
  LogoutActionCreator,
  LoginSuccessActionCreator,
  SetAuthTokenActionCreator,
} from "../state/inheritFromWeeb-ui-v2/areas/auth/state/actions";
import { isAxiosError, pushGtm } from "../../components/utils/util";
import type { ActionTypeHelper, AppDispatch } from "../state/rootStore";

import { notifyError, notifySuccess } from "./notify";
import { AUTH_2FA_USER, AUTH_USER, INIT_VISITOR_ID, NO_USER_AUTH, SWITCH_ORG, UPDATE_AVIARY_TOKENS } from "./types";

export const initVisitorId = (visitorId: string) => ({ type: INIT_VISITOR_ID, payload: visitorId });

export const updateAviaryTokens = (aviaryInfo: {
  aviary_client: string;
  aviary_token: string;
  aviary_uid: string;
  aviary_user_details: any;
}) => ({
  type: UPDATE_AVIARY_TOKENS,
  payload: aviaryInfo,
});

type LoginEventType = "sign_up" | "login" | "current_user";

const reportUserLoginEvent =
  (loginType: LoginEventType): ActionTypeHelper =>
  (_, getState) => {
    const { user_id: userId, email, full_name: fullName, organization_info: organization } = getState().auth;
    if (!localStorage.getItem("ts_user_email")) {
      localStorage.setItem("ts_user_email", email ?? "");
    }

    if (loginType !== "current_user") {
      pushGtm({
        event: loginType,
        user_id: userId,
      });
    }
    pushGtm({
      event: "user_detected",
      user_id: userId,
      user_email: email,
      user_full_name: fullName,
      organization_id: organization?._id || null,
      organization_name: organization?.organization_name || null,
    });
  };

// sign up action, stores jwt token for continued auth
export const contactUser =
  (page?: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch, getState) => {
    const contactValues = getState().form.contact.values;
    const config = {
      headers: { "Content-Type": "application/json" },
    };
    try {
      await axios.post("/contact-their-story", { ...contactValues, location: page }, config);
      dispatch(notifySuccess("Message sent to TheirStory"));
    } catch (err) {
      dispatch(notifyError("Error in sending message. Please contact TheirStory at hello@theirstory.io"));
      throw new SubmissionError(err.response.data.error);
    }
  };

// login action stores jwt token upon sucess
export const login = (): ActionTypeHelper<Promise<void>> => async (dispatch, getState) => {
  const loginValues = getState().form.login.values;
  try {
    const response = await axios.post<UserAuthResponse>("/signin", {
      ...loginValues,
      visitorId: getState().auth.visitorId,
    });
    dispatch(authenticatedAction(response.data));
    dispatch(reportUserLoginEvent("login"));
  } catch (err) {
    if (isAxiosError(err) && err.response.status === 401) {
      dispatch(notifyError("Unknown email or invalid password"));
    } else {
      dispatch(notifyError("There was an error logging you in. Please try again soon."));
      if (err.response.data.errors.length > 0) {
        dispatch(notifyError(err.response.data.errors[0]));
      }
    }

    throw new SubmissionError({});
  }
};

export const loginMagicLink =
  (value): ActionTypeHelper<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      const response = await axios.post<UserAuthResponse>("/signin", {
        email: value.email,
        password: value.password,
        visitorId: getState().auth.visitorId,
      });

      dispatch(authenticatedAction(response.data));
      dispatch(reportUserLoginEvent("login"));
    } catch (err) {
      if (isAxiosError(err) && err.response.status === 401) {
        dispatch(notifyError("Unknown email or invalid password"));
      } else {
        dispatch(notifyError("There was an error logging you in. Please try again soon."));
        if (err.response.data.errors.length > 0) {
          dispatch(notifyError(err.response.data.errors[0]));
        }
      }

      throw new SubmissionError({});
    }
  };

// sign up action, stores jwt token for continued auth
export const registerByInvite =
  (values, codeAcceptProject): ActionTypeHelper<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      const response = await axios.post<UserAuthResponse>("/registerbyinvite", {
        email: values.email,
        fullname: values.fullName,
        password: values.password,
        code: codeAcceptProject,
        newsletter: values.newsletter,
        visitorId: getState().auth.visitorId,
      });
      dispatch(authenticatedAction(response.data));
      dispatch(reportUserLoginEvent("sign_up"));
    } catch (err) {
      if (err.response && err.response.status === 422) {
        dispatch(notifyError("This email is already registered in TheirStory. Please login"));
      } else {
        dispatch(notifyError("There was an error creating your account."));
      }
      if (err.response && err.response.data && err.response.data.error) {
        throw new SubmissionError(err.response.data.error);
      } else {
        throw err;
      }
    }
  };

// sign up action, stores jwt token for continued auth
export const registerManagerByInvite =
  (values, codeAcceptOrganization): ActionTypeHelper<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      const response = await axios.post<UserAuthResponse>("/registermanagerbyinvite", {
        email: values.email,
        fullname: values.fullName,
        password: values.password,
        code: codeAcceptOrganization,
        newsletter: values.newsletter,
        visitorId: getState().auth.visitorId,
      });
      dispatch(authenticatedAction(response.data));
      dispatch(reportUserLoginEvent("sign_up"));
    } catch (err) {
      console.log(err.response);
      dispatch(notifyError(err.response.data));
      throw new SubmissionError(err.response.data.error);
    }
  };

export const currentUser = (): ActionTypeHelper<Promise<void>> => async (dispatch, getState) => {
  try {
    const superToken = localStorage.getItem("superToken");
    const token = localStorage.getItem("token") || superToken;
    const is2FACompleted = localStorage.getItem("is2FACompleted");

    dispatch({ type: NO_USER_AUTH });
    if (!token) return;

    updateApiClientJwtToken(dispatch, token);

    const response = await axios.get<AuthResponse>("/current_user", {
      params: { visitorId: getState().auth.visitorId },
    });

    // check if this is a 2fa login flow and set value to true when completed
    if (is2FACompleted) {
      dispatch(
        authenticatedAction({
          ...{ authenticated: true },
          ...response.data,
          ...(superToken && superToken !== token ? { superToken } : {}),
        })
      );
      dispatch(reportUserLoginEvent("current_user"));
      return;
    }

    // check is token is set.
    dispatch(
      authenticatedAction({
        ...response.data,
        ...(superToken && superToken !== token ? { superToken } : {}),
      })
    );
    dispatch(reportUserLoginEvent("current_user"));
  } catch (err) {
    Sentry.captureException(err);
    console.log(err);
    dispatch(signoutUser());
  }
};

// remove the local token, and call unauth for reducers
export const signoutUser = (): ActionTypeHelper => (dispatch) => {
  dispatch(LogoutActionCreator());
};

const updateApiClientJwtToken = (dispatch: AppDispatch, token?: string) => {
  if (token) {
    axios.defaults.headers.common.authorization = token;
  } else {
    delete axios.defaults.headers.common.authorization;
  }
  dispatch(SetAuthTokenActionCreator({ access_token: token }));
};

export type AuthenticatedActionParams = AuthResponse & {
  superToken?: string;
  forceTwofactorAuthentication?: boolean;
  is2FA?: boolean;
  is2FASuccess?: boolean;
  is2FACompleted?: boolean;
};

// auth the user for reducers
export const authenticatedAction =
  (userAuthInfo: AuthenticatedActionParams, isUserSwitch = false): ActionTypeHelper =>
  (dispatch, getState) => {
    if (userAuthInfo.forceTwofactorAuthentication) {
      userAuthInfo.is2FA = true;
      userAuthInfo.is2FASuccess = userAuthInfo.is2FACompleted;
    }

    if (isUserSwitch) {
      // Current user is about to switch to impersonating someone else; let's save their
      // original token as superToken in localStorage
      localStorage.setItem("superToken", getState().auth.token);
    } else {
      localStorage.removeItem("superToken");
    }

    /// Not storing token for visitors
    /// Pablo Bussi 8/18/22
    const parsedToken = decode(userAuthInfo.token, null, true);
    if (!parsedToken.sub.startsWith("visitor:")) {
      localStorage.setItem("token", userAuthInfo.token);
    }
    updateApiClientJwtToken(dispatch, userAuthInfo.token);
    if (userAuthInfo.forceTwofactorAuthentication && userAuthInfo.is2FA) {
      dispatch({ type: AUTH_2FA_USER, payload: { userAuthInfo, isUserSwitch } });
    } else {
      dispatch({ type: AUTH_USER, payload: { userAuthInfo, isUserSwitch } });
    }
    dispatch(LoginSuccessActionCreator());
  };

// password recovery action, stores token and delivers email with ephemeral token
export const passwordRecovery =
  (values): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    const passwordRecoveryValues = values;
    try {
      await axios.post("/send-password-recovery-email", passwordRecoveryValues);
      dispatch(notifySuccess("We have sent you a link to update your password"));
    } catch (err) {
      throw new SubmissionError({ email: "We couldn't send your password recovery link" });
    }
  };

// new password update action
export const passwordUpdate =
  (token: string, newPassword): ActionTypeHelper<Promise<void>> =>
  async (dispatch, getState) => {
    const visitorId = getState().auth.visitorId;
    try {
      await axios.post<UserAuthResponse>("/password/update", {
        password: newPassword,
        password_confirm: newPassword,
        token,
        visitorId,
      });
      // dispatch(authenticatedAction(response.data));
    } catch (err) {
      throw new SubmissionError(err);
    }
  };

export const switchUser =
  (email: string): ActionTypeHelper<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await axios.post<UserAuthResponse>("/auth/switchUser", { email });
      dispatch(
        authenticatedAction(
          {
            ...response.data,
            // Admin impersonation should not force the impersonated user's 2FA flow.
            forceTwofactorAuthentication: false,
          },
          true
        )
      );
      return true;
    } catch (err) {
      if (isAxiosError(err) && err.response.status === 404) {
        return false;
      }
      throw err;
    }
  };

export const clearUserImpersonation = (): ActionTypeHelper<Promise<void>> => async (dispatch, getState) => {
  const { auth } = getState();
  if (!auth.superToken) return;

  try {
    updateApiClientJwtToken(dispatch, auth.superToken);
    const response = await axios.get<AuthResponse>("/current_user", {
      params: { visitorId: getState().auth.visitorId },
    });
    dispatch(authenticatedAction(response.data));

    // When we exit impersonation, let's return the user back to the admin page where they
    // began their journey. This way we do not have to sprinkle checks in all other
    // components to see whether or not user token was changed. The only page that will
    // have to consider this, would be the admin page which is where we manage impersonation
    // to begin with so things are kinda self-contained
    // We use window.location.href to perform a hard reload and reset all frontend state.
    window.location.href = "/admin";
  } catch (err) {
    Sentry.captureException(err);
    console.log(err);
    dispatch(signoutUser());
  }
};

// get ticket information

export const getTicket = async (ticketId, visitorId) =>
  // eslint-disable-next-line no-return-await
  await axios.post(`/ticket/${ticketId}`, { visitor_id: visitorId });

/**
 * Switch the active org for a multi-org manager.
 * Updates User.organization in the DB, swaps organization_info in Redux,
 * and navigates to the new org's projects page.
 *
 * `redirectTo` defaults to the projects page (used by the sidebar switcher). When
 * syncing the active org from a legacy org-scoped deep link, pass the current URL
 * so the reload keeps the user on the same page instead of bouncing to /projects.
 */
export const switchOrg =
  (orgId: string, redirectTo = "/projects"): ActionTypeHelper<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await axios.post("/user/switch_org", { org_id: orgId });
      dispatch({ type: SWITCH_ORG, payload: response.data });
      // Use window.location.href to perform a hard reload and reset all frontend state,
      // guaranteeing all components refetch their data with the new org context.
      window.location.href = redirectTo;
      return true;
    } catch (err) {
      dispatch(notifyError("Failed to switch organization. Please try again."));
      return false;
    }
  };
